import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserCircle } from '@phosphor-icons/react';
import { MAX_INTRO_SECONDS, type ProfileResponse } from '@app/shared';
import { useLiveProfile } from '../hooks/useIntro';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { errorText } from '../lib/errors';
import { ThoughtRecorder } from './ThoughtRecorder';
import { Button, Card, Spinner } from './ui';

/**
 * The spoken introduction (spec 6.3): once, skippable. Shown while the
 * introduction is pending or being processed; the page reports how it ended.
 */
export function IntroCard({ onError }: { onError: (text: string) => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [skipping, setSkipping] = useState(false);
  const data = useLiveProfile();
  if (!data) return null;
  const status = data.user.introStatus;
  if (status !== 'pending' && status !== 'processing') return null;

  const save = (p: ProfileResponse) => qc.setQueryData<ProfileResponse>(['profile'], p);

  const send = async (blob: Blob, _mime: string, durationMs: number) => {
    try {
      save(await api.sendIntro(blob, durationMs));
      return true;
    } catch (err) {
      onError(errorText(err, t));
      return false;
    }
  };

  const skip = async () => {
    setSkipping(true);
    try {
      save(await api.skipIntro());
    } catch (err) {
      onError(errorText(err, t));
    } finally {
      setSkipping(false);
    }
  };

  return (
    <Card variant="surface" padding="none" className="p-3">
      <div className="flex items-center gap-3">
        <span className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent-soft text-accent-soft-on">
          <UserCircle size={22} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">{t.intro.title}</p>
          {status === 'processing' ? (
            <p className="flex items-center gap-1.5 text-xs text-hint mt-0.5">
              <Spinner size="sm" />
              {t.intro.processing}
            </p>
          ) : (
            <p className="text-xs text-hint mt-0.5">{t.intro.hint}</p>
          )}
        </div>
        {status === 'pending' && (
          <Button variant="ghost" size="sm" loading={skipping} onClick={skip}>
            {t.intro.skip}
          </Button>
        )}
      </div>
      {status === 'pending' && (
        <div className="mt-2 pl-[52px]">
          <ThoughtRecorder
            size="sm"
            maxSeconds={MAX_INTRO_SECONDS}
            botUrl={data.botUrl}
            onRecorded={send}
            onChatFallback={() => api.setVoiceIntent('intro')}
            onFailed={(err) => onError(errorText(err, t))}
          />
        </div>
      )}
    </Card>
  );
}
